import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Send, MapPin, CalendarDays, Wallet, ChevronDown, ChevronUp, Save, RotateCcw, Clock, Dices } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";

const SUGGESTIONS = [
  "10 days in Japan under $5000 for a couple",
  "A 5-day family trip to Dubai with kids",
  "Weekend getaway from Kochi — beaches & food",
  "2 weeks backpacking Europe on a budget",
];

/* Extract the last ```json ...``` block from an assistant message. */
export function extractItinerary(text) {
  const m = [...(text || "").matchAll(/```json\s*([\s\S]*?)```/g)];
  if (!m.length) return null;
  try {
    const it = JSON.parse(m[m.length - 1][1]);
    return it && it.title && Array.isArray(it.day_plan) ? it : null;
  } catch { return null; }
}

const stripJson = (text) => (text || "").replace(/```json[\s\S]*?```/g, "").replace(/```json[\s\S]*$/, "").trim();

function ItineraryCard({ it, onSave, saving }) {
  const [open, setOpen] = useState({ 0: true });
  return (
    <div className="mt-3 border border-emerald-300 bg-white overflow-hidden" data-testid="planner-itinerary-card">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-3">
        <div className="font-heading font-900 text-lg leading-tight">{it.title}</div>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-emerald-50">
          {it.stops?.length > 0 && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{it.stops.join(" → ")}</span>}
          {(it.start_date || it.end_date) && <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{[it.start_date, it.end_date].filter(Boolean).join(" → ")}</span>}
          {it.budget_estimate && <span className="inline-flex items-center gap-1"><Wallet className="w-3.5 h-3.5" />{it.budget_estimate}</span>}
        </div>
      </div>
      {it.summary && <p className="px-4 py-3 text-sm text-zinc-700 border-b border-zinc-100">{it.summary}</p>}
      <div className="divide-y divide-zinc-100">
        {(it.day_plan || []).map((d, i) => (
          <div key={i}>
            <button onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))} data-testid={`planner-day-${d.day || i + 1}`}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-zinc-50">
              <span className="shrink-0 w-9 h-9 bg-emerald-50 border border-emerald-200 text-emerald-700 font-heading font-900 text-sm flex items-center justify-center">D{d.day || i + 1}</span>
              <span className="flex-1 min-w-0 font-600 text-sm truncate">{d.title}{d.date ? <span className="ml-2 font-400 text-xs text-muted-foreground">{d.date}</span> : null}</span>
              {open[i] ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
            </button>
            {open[i] && (
              <div className="px-4 pb-3 space-y-1.5">
                {(d.activities || []).map((a, j) => (
                  <div key={j} className="flex items-start gap-2 text-sm">
                    <span className="shrink-0 inline-flex items-center gap-1 font-mono text-[11px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 mt-0.5"><Clock className="w-3 h-3" />{a.time || "—"}</span>
                    <span className="text-zinc-700">{a.text}{a.place ? <span className="text-muted-foreground"> · {a.place}</span> : null}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="px-4 py-3 bg-emerald-50/60 border-t border-emerald-100 flex items-center gap-3">
        <button onClick={onSave} disabled={saving} data-testid="planner-save-trip"
          className="inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 text-sm font-700 hover:bg-emerald-700 disabled:opacity-60 transition-colors">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save to My Travel Hub"}
        </button>
        <span className="text-xs text-muted-foreground">Saved trips are fully editable — drag days &amp; activities around anytime.</span>
      </div>
    </div>
  );
}

/* Quick reshape chips shown under a freshly generated itinerary.
   Short natural phrases — they read well as chat bubbles and in saved history. */
const REFINEMENTS = [
  ["Cheaper", "Make it cheaper — trim the budget but keep the best highlights"],
  ["Slower pace", "Slow the pace down — fewer activities per day, more rest and free time"],
  ["More food", "Add more food experiences — local restaurants, street food and markets"],
  ["More culture", "Add more culture — museums, historic sites and local traditions"],
  ["One day longer", "Make the trip one day longer with fitting activities"],
];

export default function TripPlannerAI() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sid] = useState(() => {
    const cur = sessionStorage.getItem("tp_planner_sid");
    if (cur) return cur;
    const s = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())).slice(0, 36);
    sessionStorage.setItem("tp_planner_sid", s);
    return s;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const endRef = useRef(null);
  const streamingRef = useRef(false); // true once the user has started any stream — history load must never clobber local turns
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (!user) return;
    api.get(`/planner/history/${sid}`).then((r) => {
      if (!streamingRef.current) setMessages(r.data.items || []);
      // /plan-ai?surprise=1 (from Travel Hub) auto-fires a Surprise Me plan once
      if (params.get("surprise") === "1") {
        setParams({}, { replace: true });
        setTimeout(() => surprise(), 300);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, sid]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, busy]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || busy) return;
    setInput("");
    await stream("/planner/chat", { session_id: sid, message }, message);
  };

  /* One-tap Surprise Me — backend invents a trip from favorites & past journeys. */
  const surprise = async () => {
    if (busy) return;
    await stream("/planner/surprise", { session_id: sid }, "Surprise me with a trip!");
  };

  const stream = async (path, payload, userText) => {
    setBusy(true);
    streamingRef.current = true;
    setMessages((m) => [...m, { role: "user", text: userText }, { role: "assistant", text: "" }]);
    // Defensive append: if any concurrent update replaced the list, re-create the assistant bubble.
    const appendToAssistant = (chunk, replace = false) => setMessages((m) => {
      const next = [...m];
      const last = next[next.length - 1];
      if (!last || last.role !== "assistant") next.push({ role: "assistant", text: chunk });
      else next[next.length - 1] = { role: "assistant", text: replace ? chunk : last.text + chunk };
      return next;
    });
    try {
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok || !res.body) throw new Error("planner unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.t === "delta") {
            appendToAssistant(ev.c);
          } else if (ev.t === "error") {
            appendToAssistant(ev.c, true);
          }
        }
      }
    } catch {
      appendToAssistant("The planner is unavailable right now — please try again shortly.", true);
    } finally {
      setBusy(false);
      // intentionally NOT resetting streamingRef — once the user has interacted,
      // the (possibly slow) history GET must never replace the local conversation
    }
  };

  const saveItinerary = async (it) => {
    setSaving(true);
    try {
      const { data } = await api.post("/planner/save", { itinerary: it });
      toast.success("Trip saved to My Travel Hub");
      navigate(`/trips/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the trip");
    } finally { setSaving(false); }
  };

  const reset = () => { sessionStorage.removeItem("tp_planner_sid"); window.location.reload(); };

  if (authLoading) return <Spinner full />;
  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center">
        <Sparkles className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
        <h1 className="font-heading font-900 text-2xl mb-2">AI Trip Planner</h1>
        <p className="text-muted-foreground mb-6">Describe your dream trip in plain words — get a complete day-by-day itinerary you can edit and save. Sign in to start planning.</p>
        <Link to="/login" data-testid="planner-signin" className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 font-700">Sign in to plan</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-5 py-6 flex flex-col" data-testid="ai-trip-planner" style={{ minHeight: "calc(100vh - 200px)" }}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 shrink-0 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center"><Sparkles className="w-6 h-6 text-white" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading font-900 text-2xl leading-tight">AI Trip Planner</h1>
          <p className="text-sm text-muted-foreground">Tell me where, how long and your budget — I'll build the whole itinerary.</p>
        </div>
        {messages.length > 0 && (
          <div className="shrink-0 flex gap-1.5">
            <button onClick={surprise} disabled={busy} data-testid="planner-surprise-header" title="Invent a trip for me"
              className="inline-flex items-center gap-1 border border-violet-300 text-violet-700 px-2.5 py-1.5 text-xs font-600 hover:border-violet-600 disabled:opacity-50">
              <Dices className="w-3.5 h-3.5" /> Surprise
            </button>
            <button onClick={reset} data-testid="planner-new-chat" title="Start a new plan"
              className="inline-flex items-center gap-1 border border-zinc-300 px-2.5 py-1.5 text-xs font-600 text-zinc-600 hover:border-primary hover:text-primary">
              <RotateCcw className="w-3.5 h-3.5" /> New plan
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 space-y-4 pb-4">
        {messages.length === 0 && (
          <div className="border border-dashed border-emerald-300 bg-emerald-50/40 p-5">
            <div className="text-sm font-700 text-emerald-800 mb-3">Try one of these:</div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="planner-suggestion"
                  className="px-3 py-1.5 bg-white border border-emerald-300 text-sm text-emerald-800 hover:border-emerald-600 transition-colors text-left">
                  {s}
                </button>
              ))}
              <button onClick={surprise} data-testid="planner-surprise"
                className="px-3 py-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-700 inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity">
                <Dices className="w-4 h-4" /> Surprise me — invent a trip for me
              </button>
            </div>
          </div>
        )}
        {messages.map((m, i) => {
          const isLast = i === messages.length - 1;
          const it = m.role === "assistant" && (!isLast || !busy) ? extractItinerary(m.text) : null;
          const display = m.role === "assistant" ? stripJson(m.text) : m.text;
          return (
            <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
              <div className={m.role === "user"
                ? "max-w-[85%] bg-primary text-white px-4 py-2.5 text-sm whitespace-pre-wrap"
                : "max-w-full"} data-testid={`planner-msg-${m.role}`}>
                {m.role === "assistant" ? (
                  <div className="flex gap-2.5">
                    <div className="w-7 h-7 shrink-0 bg-emerald-100 text-emerald-700 flex items-center justify-center mt-0.5"><Sparkles className="w-4 h-4" /></div>
                    <div className="flex-1 min-w-0">
                      {display ? <p className="text-sm text-zinc-800 whitespace-pre-wrap">{display}</p>
                        : (isLast && busy ? <span className="inline-flex gap-1 items-center text-emerald-600 text-sm">Planning<span className="animate-pulse">…</span></span> : null)}
                      {it && <ItineraryCard it={it} saving={saving} onSave={() => saveItinerary(it)} />}
                      {it && isLast && !busy && (
                        <div className="mt-2.5 flex flex-wrap items-center gap-1.5" data-testid="planner-refinements">
                          <span className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground mr-0.5">Reshape:</span>
                          {REFINEMENTS.map(([label, prompt]) => (
                            <button key={label} onClick={() => send(prompt)} disabled={busy}
                              data-testid={`planner-refine-${label.toLowerCase().replace(/\s+/g, "-")}`}
                              className="px-2.5 py-1 border border-violet-300 bg-violet-50 text-violet-800 text-xs font-600 hover:border-violet-600 transition-colors disabled:opacity-50">
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : display}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(); }}
        className="sticky bottom-16 md:bottom-4 bg-white border border-zinc-300 flex items-center gap-2 p-2 shadow-lg">
        <input value={input} onChange={(e) => setInput(e.target.value)} disabled={busy}
          placeholder='e.g. "7 days in Bali in July, mid-range budget"' data-testid="planner-input"
          className="flex-1 px-3 py-2 text-sm outline-none disabled:opacity-60" />
        <button type="submit" disabled={busy || !input.trim()} data-testid="planner-send"
          className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 text-sm font-700 disabled:opacity-50 hover:bg-emerald-700 transition-colors">
          <Send className="w-4 h-4" /> Plan
        </button>
      </form>
    </div>
  );
}
